import { Box, Stack, Typography } from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { radii } from '@/theme/tokens';
import { BackToAppsButton } from './ErrorPage';

interface EmptyStateProps {
  title: string;
  description?: string;
  /** The one right act — a primary button, or an outlined "Clear filters". */
  action?: ReactNode;
  /** A second, quieter way in beside it — a text button ("Fill from an invoice"). */
  secondaryAction?: ReactNode;
  dense?: boolean;
  /** A line icon for the tile; a generic tray when none is given. */
  icon?: ReactNode;
}

/**
 * EmptyStates board: a line icon in a 56px neutral tile (14px corners), a
 * 16px title, one sentence of at most 280px, and the one right action — or
 * none when the reader has nothing to do. No illustration, no colour.
 *
 * Three shapes, all this component: first run ("No patients yet" + "Add a
 * patient"), filtered ("No results" + an outlined "Clear filters"), and
 * nothing to do ("No notifications", no action at all). The fourth, a
 * section the viewer's role does not open, is `NoAccessState` below.
 */
export function EmptyState({ title, description, action, secondaryAction, dense, icon }: EmptyStateProps) {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={1.5}
      sx={{ py: dense ? 4 : 7, px: 3, textAlign: 'center' }}
    >
      <Box
        aria-hidden
        sx={{
          width: dense ? 48 : 56,
          height: dense ? 48 : 56,
          borderRadius: `${radii.xl + 2}px`,
          bgcolor: 'brand.well',
          color: 'text.secondary',
          display: 'grid',
          placeItems: 'center',
          '& svg': { fontSize: dense ? 22 : 26 },
        }}
      >
        {icon ?? <InboxOutlinedIcon />}
      </Box>
      <Typography sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 }}>
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280, lineHeight: 1.6 }}>
          {description}
        </Typography>
      ) : null}
      {action || secondaryAction ? (
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" sx={{ pt: 0.5 }}>
          {action}
          {secondaryAction}
        </Stack>
      ) : null}
    </Stack>
  );
}

/**
 * A section the viewer's role does not open (EmptyStates board): a lock, a
 * sentence that says who grants it, and the way back to the apps — never a
 * button that would only refuse again.
 */
export function NoAccessState({ title, description, dense }: { title?: string; description?: string; dense?: boolean }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      dense={dense}
      icon={<LockOutlinedIcon />}
      title={title ?? t('emptyState.noAccess.title')}
      description={description ?? t('emptyState.noAccess.body')}
      action={
        <Box sx={{ minWidth: 0 }}>
          <BackToAppsButton variant="outlined" />
        </Box>
      }
    />
  );
}
